<template>
    <div>
        <el-table class="w-full"
                  row-key="id"
                  :data="botsData"
                  stripe
                  :row-class-name="getRowClassName"
                  data-testid="aloai-bots-table">
            <el-table-column type="expand" data-testid="aloai-bot-expand-column">
                <template v-slot="scope">
                    <div class="row align-items-top">
                        <div class="col-md-6">
                            <span class="text-greyish break-word" :data-testid="'aloai-bot-description-' + scope.row.id">
                                <span>{{ scope.row.description ?? '-' }}</span>
                            </span>
                        </div>
                        <div class="col-md-6 b-l">
                            <p :data-testid="'aloai-bot-created-by-' + scope.row.id">
                                <b>Created By:</b> <span>{{ scope.row.is_system_bot ? 'Aloware' : (scope.row.created_by ? (scope.row.created_by.name + ' - #' + scope.row.created_by.id) : '-') }}</span>
                            </p>
                            <p :data-testid="'aloai-bot-modified-by-' + scope.row.id">
                                <b>Last Modified By:</b> <span>{{ scope.row.modified_by ? (scope.row.modified_by.name + ' - #' + scope.row.modified_by.id) : '-' }}</span>
                            </p>
                        </div>
                    </div>
                </template>
            </el-table-column>

            <el-table-column
                label="Name"
                align="left"
                :min-width="150"
                data-testid="aloai-bot-name-column">
                <template v-slot="scope">
                    <div class="d-flex">
                        <el-tooltip
                            class="mr-4 align-self-center"
                            effect="dark"
                            :content="getToggleTooltipContent(scope.row)"
                            :disabled="canToggleBot(scope.row)"
                            placement="top"
                            :data-testid="'aloai-bot-enable-tooltip-' + scope.row.id">
                            <el-switch
                                v-model="scope.row.enabled"
                                active-color="#00BF50"
                                :disabled="!canToggleBot(scope.row)"
                                @change="updateBotEnabled(scope.row)"
                                :data-testid="'aloai-bot-enable-switch-' + scope.row.id"
                            />
                        </el-tooltip>
                        <div>
                            <router-link :to="canEditAgent(scope.row) ? {
                                    name: 'AloAi: Configure an agent',
                                    params: {bot_id: scope.row.id}
                                } : ''"
                                :data-testid="'aloai-bot-name-link-' + scope.row.id">
                                <div class="hover-lower-opacity">
                                    <p class="mb-0">
                                        <b>{{ scope.row.name }}</b>
                                    </p>
                                    <div class="d-flex align-items-center">
                                        <span>#{{ scope.row.id }}</span>
                                        <el-tooltip v-if="scope.row.is_system_bot" content="This is a system-managed agent." placement="top">
                                            <span class="label system-badge ml-2" :data-testid="'aloai-bot-system-badge-' + scope.row.id">
                                                System
                                            </span>
                                        </el-tooltip>
                                    </div>
                                </div>
                            </router-link>
                        </div>
                    </div>
                </template>
            </el-table-column>

            <el-table-column
                width="100"
                label="Type"
                align="center"
                data-testid="aloai-bot-type-column">
                <template v-slot="scope">
                    <span class="ml-2 d-inline-block label blue" :data-testid="'aloai-bot-type-' + scope.row.id">
                        {{ scope.row.type_text }}
                    </span>
                </template>
            </el-table-column>

            <el-table-column
                width="150"
                label="Direction"
                align="left"
                data-testid="aloai-bot-direction-column">
                <template v-slot="scope">
                    <span :data-testid="'aloai-bot-direction-' + scope.row.id">{{ scope.row.direction_text }}</span>
                </template>
            </el-table-column>

            <el-table-column
                width="200"
                label="Model"
                align="left"
                data-testid="aloai-bot-model-column">
                <template v-slot="scope">
                    <div>
                        <div>
                            <span v-if="scope.row.type === AloAi.TYPE_TEXT" :data-testid="'aloai-bot-model-' + scope.row.id">{{ scope.row.model_human_readable_text }}</span>
                            <span v-else :data-testid="'aloai-bot-voice-model-' + scope.row.id">{{ scope.row.voice_model_human_readable_text }}</span>
                        </div>
                        <div v-if="scope.row.price_per_minute && scope.row.price_unit" class="mt-1">
                            <!-- Voice bots: show tier and price -->
                            <template v-if="scope.row.type === AloAi.TYPE_VOICE && scope.row.tier">
                                <el-tag
                                    :type="getTierTagType(scope.row.tier)"
                                    size="mini"
                                    class="mr-1"
                                    :data-testid="'aloai-bot-tier-' + scope.row.id">
                                    {{ scope.row.tier }}
                                </el-tag>
                                <span class="text-muted small" :data-testid="'aloai-bot-price-' + scope.row.id">
                                    ${{ parseFloat(scope.row.price_per_minute).toFixed(2) }}/{{ scope.row.price_unit }}
                                </span>
                            </template>
                        </div>
                    </div>
                </template>
            </el-table-column>

            <el-table-column
                min-width="100"
                label="Lines"
                align="left"
                data-testid="aloai-bot-lines-column">
                <template v-slot="scope">
                    <span v-if="scope.row.is_system_bot && !scope.row.is_visible_in_list" class="text-muted" :data-testid="'aloai-bot-system-lines-' + scope.row.id">
                        —
                    </span>
                    <a v-else>
                        <span class="text-dark-greenish">
                            <span v-if="scope.row.campaigns && scope.row.campaigns.length === 1">
                                <router-link
                                    :to="{
                                        name: 'Line Settings',
                                        params: {
                                            campaign_id: scope.row.campaigns[0].id
                                        },
                                        query: {
                                            tab: 'routing-settings'
                                        }
                                    }"
                                    class="text-dark-greenish"
                                    :data-testid="'aloai-bot-line-link-' + scope.row.id">
                                    <span :data-testid="'aloai-bot-line-name-' + scope.row.id">{{ scope.row.campaigns[0]?.name }}</span>
                                    <br/>
                                    <span v-if="scope.row.campaigns[0]?.incoming_numbers && scope.row.campaigns[0].incoming_numbers.length" :data-testid="'aloai-bot-line-number-' + scope.row.id">
                                        {{ scope.row.campaigns[0].incoming_numbers[0]?.phone_number | fixPhone }}
                                    </span>
                                </router-link>
                            </span>
                            <el-popover
                                v-else-if="scope.row.campaigns && scope.row.campaigns.length > 1"
                                placement="bottom"
                                trigger="hover"
                                popper-class="lines-popover"
                                :data-testid="'aloai-bot-lines-count-' + scope.row.id">
                                <div class="lines-list">
                                    <div 
                                        v-for="campaign in scope.row.campaigns" 
                                        :key="campaign.id" 
                                        class="line-item"
                                        @click="navigateToLine(campaign.id)">
                                        <i class="el-icon-phone"></i>
                                        <div class="line-content">
                                            <div class="line-name">{{ campaign.name }}</div>
                                            <div v-if="campaign.incoming_numbers && campaign.incoming_numbers.length" class="line-number">
                                                {{ campaign.incoming_numbers[0]?.phone_number | fixPhone }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <span slot="reference" class="lines-count-link text-dark-greenish">
                                    {{ scope.row.campaigns.length }} Lines
                                </span>
                            </el-popover>
                            <span v-else :data-testid="'aloai-bot-lines-count-' + scope.row.id">
                                {{ scope.row.campaigns.length }} Lines
                            </span>
                        </span>
                    </a>
                </template>
            </el-table-column>

            <el-table-column
                align="left"
                label="Status"
                :min-width="40"
                data-testid="aloai-bot-status-column">
                <template v-slot="scope">
                    <span v-if="scope.row.enabled"
                          class="text-green"
                          :data-testid="'aloai-bot-status-running-' + scope.row.id">
                        <i class="fa fa-play-circle text-success mr-1"></i>
                        <span>Running</span>
                    </span>
                    <span v-else
                          class="text-red"
                          :data-testid="'aloai-bot-status-paused-' + scope.row.id">
                        <i class="fa fa-pause-circle text-warning mr-1"></i>
                        <span>Paused</span>
                    </span>
                </template>
            </el-table-column>

            <el-table-column
                label="Operations"
                align="center"
                width="180"
                fixed="right"
                data-testid="aloai-bot-operations-column">
                <template v-slot="scope">
                    <div class="col-12">
                        <router-link
                            :to="{
                                name: 'User Activity',
                                params: {user_id: scope.row.user_id}
                            }"
                            :data-testid="'aloai-bot-activity-link-' + scope.row.id">
                            <button class="btn btn-sm btn-block purplish" :data-testid="'aloai-bot-activity-btn-' + scope.row.id">
                                <i class="fa fa-signal pull-left"></i>
                                Activity
                            </button>
                        </router-link>
                    </div>
                    <div class="col-12 mt-1">
                        <router-link
                            :to="{
                                name: 'AloAi: Configure an agent',
                                params: {bot_id: scope.row.id}
                            }"
                            :data-testid="'aloai-bot-edit-link-' + scope.row.id"
                        >
                            <el-tooltip
                                :content="getEditTooltipContent(scope.row)"
                                :disabled="canEditAgent(scope.row)"
                                placement="top"
                                :data-testid="'aloai-bot-edit-tooltip-' + scope.row.id"
                            >
                                <button
                                    class="btn btn-sm btn-block info"
                                    :disabled="!canEditAgent(scope.row)"
                                    :data-testid="'aloai-bot-edit-btn-' + scope.row.id"
                                >
                                    <i class="fa fa-pencil pull-left"></i>
                                    Edit
                                </button>
                            </el-tooltip>
                        </router-link>
                    </div>
                    <div class="col-12 mt-1">
                        <el-tooltip
                            v-if="getDeleteTooltip(scope.row)"
                            :content="getDeleteTooltip(scope.row)"
                            placement="bottom"
                            :data-testid="'aloai-bot-delete-tooltip-' + scope.row.id"
                        >
                            <button
                                class="btn btn-sm btn-block danger"
                                :disabled="!canDeleteBot(scope.row)"
                                :data-testid="'aloai-bot-delete-btn-' + scope.row.id"
                                @click="canDeleteBot(scope.row) && handleDelete(scope.row)"
                            >
                                <i class="fa fa-trash pull-left"></i>
                                Delete
                            </button>
                        </el-tooltip>
                        <button
                            v-else
                            class="btn btn-sm btn-block danger"
                            :disabled="!canDeleteBot(scope.row)"
                            :data-testid="'aloai-bot-delete-btn-' + scope.row.id"
                            @click="handleDelete(scope.row)"
                        >
                            <i class="fa fa-trash pull-left"></i>
                            Delete
                        </button>
                    </div>
                </template>
            </el-table-column>
        </el-table>
    </div>
</template>

<script>
import axios from 'axios'
import * as AloAi from '../../constants/aloai'
import { aloai_mixin } from '../../mixins'

const NOT_BUILD = 1
const BUILD_SUCCESS = 2
const BUILD_IN_PROGRESS = 3
const BUILD_FAILED = 4

export default {
    
    props: {
        bots: {
            required: false,
            default: []
        }
    },

    mixins: [aloai_mixin],

    data() {
        return {
            NOT_BUILD,
            BUILD_SUCCESS,
            BUILD_IN_PROGRESS,
            BUILD_FAILED,
            delete_bot_visible: false,
            bot: null,
            count: 0,
            export_disabled: false,
            delete_disabled: false,
            // Todo: implement both search and pagination
            search: '',
            filters: {
                page: 1,
                size: 5,
            },
            AloAi
        }
    },

    computed: {
        // Todo: implement search logic
        botsData() {
            if (!this.bots) {
                return this.bots
            }
            return this.bots.filter(data => !this.search || data.name.toLowerCase().includes(this.search.toLowerCase()))
        }
    },


    methods: {
        disableTooltipContent(bot) {
            if (bot.type === AloAi.TYPE_TEXT) {
                return 'Text agent feature is disabled'
            }

            if (bot.type === AloAi.TYPE_VOICE) {
                if (bot.direction === AloAi.DIRECTION_INBOUND) {
                    return 'Inbound voice agent feature is disabled'
                }

                if (bot.direction === AloAi.DIRECTION_OUTBOUND) {
                    return 'Outbound voice agent feature is disabled'
                }
            }
        },

        getEditTooltipContent(bot) {
            // Externally managed bots
            if (bot.is_externally_managed) {
                return 'This agent is externally managed'
            }

            // AI Call Rescue bot shows guidance message
            if (bot.internal_type === AloAi.INTERNAL_TYPE_MISSED_CALL_HANDLER) {
                return 'Manage availability in Account Settings → Calling.'
            }

            // Use existing disable tooltip content for other agents
            return this.disableTooltipContent(bot)
        },

        getRowClassName({ row }) {
            if (row.is_system_bot) {
                return 'system-bot-row'
            }
            return ''
        },

        getToggleTooltipContent(bot) {
            // Non-toggleable system bots cannot be toggled
            if (AloAi.NON_TOGGLEABLE_INTERNAL_TYPES.includes(bot.internal_type)) {
                return 'System Agents cannot be enabled/disabled manually'
            }
            
            // IVA Agents with connected lines cannot be toggled
            if (this.isIvaBotWithConnectedLines(bot)) {
                return 'To pause a IVA agent, first disconnect it from all lines.'
            }
            
            // Use existing disable tooltip content for non-system Agents
            return this.disableTooltipContent(bot)
        },

        canToggleBot(bot) {
            // Externally managed bots CAN be toggled from Aloware
            if (bot.is_externally_managed) {
                return true
            }

            // Non-toggleable system bots cannot be toggled
            if (AloAi.NON_TOGGLEABLE_INTERNAL_TYPES.includes(bot.internal_type)) {
                return false
            }

            // IVA Agents with connected lines cannot be toggled
            if (this.isIvaBotWithConnectedLines(bot)) {
                return false
            }

            // Use existing canEditAgent logic for non-system Agents
            return this.canEditAgent(bot)
        },
        canEditAgent(bot) {
            // Externally managed bots cannot be edited from Aloware
            if (bot.is_externally_managed) {
                return false
            }

            // Bots with internal_type can always be edited
            if (bot.internal_type) {
                return true
            }

            if (bot.type === AloAi.TYPE_TEXT) {
                return this.isAloAiTextAgentsEnabled()
            }

            if (bot.type === AloAi.TYPE_VOICE) {
                if (bot.direction === AloAi.DIRECTION_INBOUND) {
                    return this.isAloAiInboundVoiceAgentsEnabled()
                }

                if (bot.direction === AloAi.DIRECTION_OUTBOUND) {
                    return this.isAloAiOutboundVoiceAgentsEnabled()
                }

                return this.isAloAiVoiceAgentsEnabled()
            }

            return true
        },
        canDeleteBot(bot) {
            // Externally managed bots cannot be deleted from Aloware
            if (bot.is_externally_managed) {
                return false
            }
            // Non-deletable system bots cannot be deleted
            if (AloAi.NON_DELETABLE_INTERNAL_TYPES.includes(bot.internal_type)) {
                return false
            }
            // IVA with connected lines cannot be deleted
            if (this.isIvaBotWithConnectedLines(bot)) {
                return false
            }
            return true
        },

        getDeleteTooltip(bot) {
            // Externally managed bots
            if (bot.is_externally_managed) {
                return 'This agent is externally managed'
            }
            // Non-deletable system bots cannot be deleted
            if (AloAi.NON_DELETABLE_INTERNAL_TYPES.includes(bot.internal_type)) {
                return 'System Agents cannot be deleted'
            }
            if (this.isIvaBotWithConnectedLines(bot)) {
                return 'To delete a IVA agent, first disconnect it from all lines.'
            }
            return null
        },
        
        isIvaBot(bot) {
            return bot.internal_type === AloAi.INTERNAL_TYPE_IVA
        },
        
        isIvaBotWithConnectedLines(bot) {
            const isIva = this.isIvaBot(bot)
            const hasConnectedLines = bot.campaigns && bot.campaigns.length > 0
            return isIva && hasConnectedLines
        },

        updateBotEnabled(bot) {
            this.setLoading(true)

            // Get only required parameters
            let payload = _.pick(bot, ['enabled'])

            axios.patch(`/api/v1/aloai/bot/${bot.id}/toggle-active`, payload).then(res => {
                this.setLoading(false)
                this.$notify.success({
                    offset: 95,
                    title: 'AloAi Agent',
                    message: 'Agent updated successfully.',
                })
                this.$emit('bot-update', bot)
            }).catch(err => {
                console.log(err)
                this.setLoading(false)
                this.$emit('bot-update', bot)
                this.$root.handleErrors(err.response)
            })
        },

        handleEdit(row) {
            // Each row is a bot object
            this.bot = row
            // Goes to the Bot Settings page
            this.$router.push({
                name: 'AloAi: Configure an agent',
                params: {
                    bot_id: this.bot.id
                }
            })
        },

        handleDelete(row) {
            this.$emit('bot-delete', row.id)
        },

        setLoading(loading) {
            this.export_disabled = loading
            this.delete_disabled = loading
        },

        navigateToLine(campaignId) {
            this.$router.push({
                name: 'Line Settings',
                params: {
                    campaign_id: campaignId
                },
                query: {
                    tab: 'routing-settings'
                }
            })
        }
    },
}
</script>

<style scoped>
.system-bot-row {
    background-color: #e8f4fd !important;
    border-left: 4px solid #007bff !important;
}

.system-bot-row td {
    background-color: #e8f4fd !important;
}

.system-bot-row:hover td {
    background-color: #d1ecf1 !important;
}

.system-badge {
    background-color: #007bff;
    color: white;
    font-size: 10px;
    padding: 2px 6px;
    border-radius: 3px;
    font-weight: 500;
    display: inline-block;
}
</style>

<style>
/* Global styles to override table defaults */
.el-table .system-bot-row {
    background-color: #e8f4fd !important;
}

.el-table .system-bot-row td {
    background-color: #e8f4fd !important;
}

.el-table .system-bot-row:hover td {
    background-color: #d1ecf1 !important;
}

/* Lines count link styling */
.lines-count-link {
    cursor: pointer;
    transition: opacity 0.2s;
    display: inline-flex;
    align-items: center;
}

.lines-count-link:hover {
    opacity: 0.8;
}

.lines-count-link i {
    font-size: 12px;
}
</style>

<style>
/* Lines popover styling - must be in global style to affect el-popover */
.lines-popover {
    padding: 4px 0 !important;
    min-width: 220px;
    max-width: 300px;
}

.lines-popover .lines-list {
    max-height: 260px;
    overflow-y: auto;
}

.lines-popover .line-item {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 8px 12px;
    cursor: pointer;
    transition: background-color 0.15s;
}

.lines-popover .line-item:hover {
    background-color: #f5f7fa;
}

.lines-popover .line-item > i {
    color: #67c23a;
    font-size: 14px;
    margin-top: 2px;
    flex-shrink: 0;
}

.lines-popover .line-content {
    flex: 1;
    min-width: 0;
}

.lines-popover .line-name {
    color: #606266;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.4;
    margin-bottom: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.lines-popover .line-number {
    color: #909399;
    font-size: 11px;
    line-height: 1.3;
}

/* Custom scrollbar for lines list */
.lines-popover .lines-list::-webkit-scrollbar {
    width: 5px;
}

.lines-popover .lines-list::-webkit-scrollbar-track {
    background: #f5f7fa;
}

.lines-popover .lines-list::-webkit-scrollbar-thumb {
    background: #dcdfe6;
    border-radius: 2px;
}

.lines-popover .lines-list::-webkit-scrollbar-thumb:hover {
    background: #c0c4cc;
}
</style>
